/* eslint-disable react/require-default-props */
import { Select } from '@components/Select';
import { IconButton, SelectChangeEvent, Stack, Typography } from '@mui/material';
import { theme } from '@src/theme';
import {
  buildDataTestIdAttribute,
  buildFullDataTestId,
  DATA_TEST_ID_SELECTION,
  hexOpacity,
  replaceCebabCase,
} from '@src/utils';
import { CriteriaTime, OperatinonsSign, RuleOperationType, RuleTypes } from '@src/api/selection';
import { forwardRef, useContext } from 'react';
import { ruleItems } from './RuleItem.const';
import { RuleFactory } from './components';
import { ReactComponent as PlusIcon } from '@icons/plus.svg';
import { ReactComponent as MinusIcon } from '@icons/minus.svg';
import { Controller, get, useFormContext } from 'react-hook-form';
import { DisableContext } from '../DisableContext';

type RuleItemProps = {
  rule?: any;
  prefix: string;
  operationType?: RuleOperationType | string;
  append: any;
  remove: any;
  insert: any;
  isLast: boolean;
  withoutDelete: boolean;
  prefixDataTestId?: string;
};

const getDefaultValues = (value: RuleTypes, isLast: boolean, nextOperation) => {
  const inAllRules = {
    ...(isLast ? {} : { nextOperation: nextOperation }),
    criteriaRuleType: value,
  };
  switch (value) {
    case RuleTypes.PatientAgeRule: {
      return {
        ...inAllRules,
        compareOperator: OperatinonsSign.Equals,
        value: null,
      };
    }
    case RuleTypes.PatientBirthSexRule: {
      return {
        ...inAllRules,
        birthSexes: [],
      };
    }
    case RuleTypes.ConditionAndDiagnosisRule: {
      return {
        ...inAllRules,
        scaleType: CriteriaTime.Day,
        scaleValue: null,
        diagnosisIds: [],
        diagnosisGroupIds: [],
      };
    }
    case RuleTypes.MissingResultRule: {
      return {
        ...inAllRules,
        scaleType: CriteriaTime.Day,
        scaleValue: null,
        loincItemId: null,
      };
    }
    case RuleTypes.ResultRefValueRule: {
      return {
        ...inAllRules,
        scaleType: CriteriaTime.Day,
        scaleValue: null,
        loincItemId: null,
        resultFlags: [],
      };
    }
    case RuleTypes.ResultRule: {
      return {
        ...inAllRules,
        scaleType: CriteriaTime.Day,
        scaleValue: null,
        loincItemId: null,
        loincUnitId: null,
        rangeValues: { from: null, to: null },
      };
    }
  }
};

export const RuleItem = forwardRef<HTMLDivElement, RuleItemProps>(
  ({ rule = {}, prefix, append, insert, remove, operationType = '', isLast, withoutDelete, prefixDataTestId }, ref) => {
    const { setValue, watch, control, clearErrors, formState } = useFormContext();
    const disabled = useContext(DisableContext);
    const indexRule = +(prefix.split('.').at(-1) || 0);
    const prevOperation = indexRule ? watch(`criterias.${indexRule - 1}.nextOperation`) : '';
    const nextOperation = watch(`criterias.${indexRule}.nextOperation`);

    const dataTestId = replaceCebabCase(prefix);

    const handleChange = (e: SelectChangeEvent) => {
      const { value } = e.target;
      setValue(`${prefix}`, isLast ? {} : { nextOperation: nextOperation });
      setValue(`${prefix}.criteriaRuleType`, value, { shouldValidate: true });
      clearErrors(prefix);

      const defaultValues = getDefaultValues(value as RuleTypes, isLast, nextOperation);

      setValue(`${prefix}`, defaultValues);
    };

    const addRule = () => {
      setValue(`${prefix}.nextOperation`, RuleOperationType.And);
      if (isLast) {
        append({ nextOperation: '' });
      } else
        insert(indexRule + 1, {
          nextOperation: RuleOperationType.And,
          criteriaRuleType: '',
        });
    };

    const deleteRule = () => {
      const newValue =
        prevOperation === RuleOperationType.Or && nextOperation === RuleOperationType.Or
          ? RuleOperationType.Or
          : operationType
          ? RuleOperationType.And
          : null;
      if (isLast) {
        setValue(`criterias.${indexRule - 1}.nextOperation`, null);
      } else if (indexRule > 0) {
        setValue(`criterias.${indexRule - 1}.nextOperation`, newValue);
      }
      remove(indexRule);
    };

    const diagnosesRuleError =
      (get(formState.errors, `${prefix}.diagnosisGroupIds`) || get(formState.errors, `${prefix}.diagnosisIds`)) &&
      rule?.criteriaRuleType === RuleTypes.ConditionAndDiagnosisRule;

    return (
      <Stack
        direction="row"
        gap="18px"
        bgcolor={
          disabled ? theme.palette.secondary['200'] : diagnosesRuleError ? '#FDECEF' : theme.palette.secondary['300']
        }
        padding="18px 24px"
        justifyContent="space-between"
        alignItems="flex-end"
        position="relative"
        ref={ref}
        marginTop="-1px"
        border={`1px solid ${
          disabled ? theme.palette.secondary['400'] : hexOpacity(theme.palette.secondary.main, '28%')
        }`}
        sx={
          operationType === RuleOperationType.Or
            ? {
                '&:before': {
                  content: '""',
                  position: 'absolute',
                  background: disabled ? theme.palette.secondary['600'] : theme.palette.secondary.main,
                  borderRadius: '2px 0 0 2px',
                  height: '2px',
                  width: '12px',
                  top: '50%',
                  left: '-12px',
                },
              }
            : {}
        }
      >
        <Stack gap="24px" width="100%">
          <Stack direction="row" gap="18px" flexWrap="wrap" flexGrow={1}>
            {disabled ? (
              <></>
            ) : (
              <Controller
                name={`${prefix}.criteriaRuleType`}
                control={control}
                defaultValue={''}
                render={({ field: { value }, formState: typeFormState }) => {
                  const errorField = get(typeFormState.errors, `${prefix}.criteriaRuleType`);
                  const error = errorField ? (errorField.message as string) : '';

                  return (
                    <Select
                      value={value || ''}
                      options={ruleItems}
                      maxWidth={value ? 329 : 278}
                      onChange={handleChange}
                      error={error}
                      disabled={disabled}
                      sxSelect={{
                        '&.Mui-focused:not(.Mui-disabled, .Mui-error)': {
                          border: '2px solid #858D9C',
                        },
                      }}
                      dataTestId={buildFullDataTestId(DATA_TEST_ID_SELECTION, `${dataTestId}_input-rule-type`)}
                    />
                  );
                }}
              />
            )}

            <RuleFactory
              ruleType={rule?.criteriaRuleType}
              prefix={prefix}
              dataTestId={prefixDataTestId}
              children={
                disabled ? (
                  <></>
                ) : (
                  <Stack direction="row" width="84px" minWidth="84px" marginLeft="auto" justifyContent="flex-end">
                    {!withoutDelete && (
                      <IconButton
                        onClick={deleteRule}
                        {...buildDataTestIdAttribute(prefixDataTestId, `${dataTestId}_button-delete`)}
                      >
                        <MinusIcon />
                      </IconButton>
                    )}
                    {nextOperation !== RuleOperationType.Or && (
                      <IconButton
                        onClick={addRule}
                        {...buildDataTestIdAttribute(prefixDataTestId, `${dataTestId}_button-add`)}
                      >
                        <PlusIcon />
                      </IconButton>
                    )}
                  </Stack>
                )
              }
            />
          </Stack>
          {diagnosesRuleError && (
            <Typography color="error.200" fontSize="12px" fontWeight={600} lineHeight="16px">
              Please select a diagnosis or a group of diagnosis to proceed.
            </Typography>
          )}
        </Stack>
      </Stack>
    );
  },
);
RuleItem.displayName = 'RuleItem';
